// src/pages/returns/ReturnDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Grid, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import WrapperSection from "../../components/global/WrapperSection";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import LoadingPage from "../../components/global/LoadingPage";
import { useGetReturnById } from "../../hooks/returns/useGetReturnById";

import ReturnHeader from "../../components/returns/Detail/ReturnHeader";
import ReturnItemsTable from "../../components/returns/Detail/ReturnItemsTable";
import ReturnShippingCard from "../../components/returns/Detail/ReturnShippingCard";
import ReturnActionsPanel from "../../components/returns/Detail/ReturnActionsPanel";
import ReturnTimeline from "../../components/returns/Detail/ReturnTimeline";

const ReturnDetail = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const { returnRequest, isLoading, error } = useGetReturnById(id);

    // Approved-qty drafts; only meaningful when status === 'pending'.
    const [editableQuantities, setEditableQuantities] = useState({});

    // Seed editable qtys from server response.
    useEffect(() => {
        if (!returnRequest?.items) return;
        setEditableQuantities(
            returnRequest.items.reduce((acc, it) => {
                acc[it.id] = it.approved_qty ?? it.qty ?? it.requested_qty ?? 0;
                return acc;
            }, {})
        );
    }, [returnRequest]);

    if (isLoading) return <LoadingPage />;
    if (error || !returnRequest) {
        return (
            <WrapperSection>
                <GlobalBreadcrumbs />
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="error">
                        {isArabic ? "تعذر تحميل تفاصيل المرتجع" : "Failed to load return details"}
                    </Typography>
                </Box>
            </WrapperSection>
        );
    }

    const handleApprovedQtyChange = (itemId, qty) => {
        setEditableQuantities((prev) => ({ ...prev, [itemId]: qty }));
    };

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />

            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={8}>
                    <ReturnHeader data={returnRequest} />

                    <ReturnItemsTable
                        items={returnRequest.items || []}
                        status={returnRequest.status}
                        editableQuantities={editableQuantities}
                        onApprovedQtyChange={handleApprovedQtyChange}
                    />

                    <ReturnShippingCard data={returnRequest} />

                    <ReturnTimeline events={returnRequest.events || returnRequest.history || []} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <ReturnActionsPanel
                        data={returnRequest}
                        editableQuantities={editableQuantities}
                    />
                </Grid>
            </Grid>
        </WrapperSection>
    );
};

export default ReturnDetail;
